import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { ChevronRight, Droplets, Star, Shield, Clock, Briefcase, LayoutDashboard, Sparkles, CheckSquare, Square, ExternalLink, Mail, Phone, Globe, Car } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SLOGANS } from "@/components/shared/BrandComponents";
import { useTranslation } from "react-i18next";
import SloganTicker from "@/components/shared/SloganTicker";
import LanguageSelector from "@/components/shared/LanguageSelector";
import { ThemeToggle } from "@/components/shared/ThemeToggle";
import { SocialMediaLinks } from "@/components/shared/LegalFooter";

// Custom vehicle SVG icons — same as PremiumHomePage
const CarSuvIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M5 17H3a2 2 0 01-2-2v-3a2 2 0 012-2h1l2.5-4h9L16 10h1a2 2 0 012 2v3a2 2 0 01-2 2h-2" />
    <circle cx="7.5" cy="17" r="2" /><circle cx="16.5" cy="17" r="2" />
  </svg>
);
const BoatIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 17h18l-2-5H5L3 17z" />
    <path d="M9 12V8h4v4" /><path d="M11 8V4" /><path d="M9 4h4" />
    <path d="M2 20c2-1.5 4-1.5 6 0s4 1.5 6 0 4-1.5 6 0" />
  </svg>
);
const RvIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="1" y="6" width="17" height="11" rx="2" />
    <path d="M18 9h3l2 4v4h-5V9z" />
    <circle cx="5.5" cy="17" r="1.5" /><circle cx="13.5" cy="17" r="1.5" /><circle cx="20.5" cy="17" r="1.5" />
  </svg>
);
const FleetIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M1 4h14v11H1z" />
    <path d="M15 8h4l3 4v3h-7V8z" />
    <circle cx="4.5" cy="17.5" r="1.5" /><circle cx="11.5" cy="17.5" r="1.5" /><circle cx="19.5" cy="17.5" r="1.5" />
  </svg>
);
const GolfCartIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="4" y="4" width="7" height="3" rx="1" />
    <path d="M4 7v3l3 3h7l2-4H4z" />
    <path d="M4 13h16" />
    <circle cx="6.5" cy="17" r="2" /><circle cx="15.5" cy="17" r="2" />
  </svg>
);
const RecreationalIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="5" cy="17" r="3" /><circle cx="19" cy="17" r="3" />
    <path d="M5 17h14" />
    <path d="M14 7h2l1.5 3.5" />
    <path d="M14 7l-1.5-2.5h-3L8 7l-1 4h9l-2-4z" />
  </svg>
);

const WelcomeScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showAgreementError, setShowAgreementError] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      // Check if there's a pending booking to restore — redirect to booking flow, not dashboard
      const pendingPath = localStorage.getItem('intendedPath') || sessionStorage.getItem('intendedPath');
      if (pendingPath && pendingPath.startsWith('/book')) {
        localStorage.removeItem('intendedPath');
        sessionStorage.removeItem('intendedPath');
        navigate(pendingPath);
      } else {
        navigate("/dashboard");
      }
    }
  }, [user, navigate]);

  const handleGetStarted = () => {
    if (!agreedToTerms) {
      setShowAgreementError(true);
      return;
    }
    setShowAgreementError(false);
    // Store agreement acceptance intent for after OAuth
    sessionStorage.setItem('pendingAgreement', 'true');
    sessionStorage.setItem('agreementVersion', '1.0');
    login();
  };

  const handlePortalClick = (path) => {
    if (user) {
      navigate(path);
    } else {
      // For admin portal, go directly to email login with admin intent
      if (path === '/admin') {
        sessionStorage.setItem('intendedPath', path);
        navigate('/auth/login?admin=true');
        return;
      }
      
      if (!agreedToTerms) {
        setShowAgreementError(true);
        return;
      }
      setShowAgreementError(false);
      // Only set intendedPath if not already set (e.g., from booking flow redirect)
      if (!localStorage.getItem('intendedPath') && !sessionStorage.getItem('intendedPath')) {
        localStorage.setItem('intendedPath', path);
        sessionStorage.setItem('intendedPath', path);
      }
      sessionStorage.setItem('pendingAgreement', 'true');
      sessionStorage.setItem('agreementVersion', '1.0');
      login();
    }
  };

  const slides = [
    {
      icon: <CarSuvIcon className="w-16 h-16" />,
      title: t("welcome_screen.slide_car_title"),
      description: t("welcome_screen.slide_car_desc"),
      vehicleId: "car",
    },
    {
      icon: <BoatIcon className="w-16 h-16" />,
      title: t("welcome_screen.slide_boat_title"),
      description: t("welcome_screen.slide_boat_desc"),
      vehicleId: "boat",
    },
    {
      icon: <RvIcon className="w-16 h-16" />,
      title: t("welcome_screen.slide_rv_title"),
      description: t("welcome_screen.slide_rv_desc"),
      vehicleId: "rv",
    },
    {
      icon: <FleetIcon className="w-16 h-16" />,
      title: t("welcome_screen.slide_fleet_title"),
      description: t("welcome_screen.slide_fleet_desc"),
      vehicleId: "fleet",
    },
    {
      icon: <GolfCartIcon className="w-16 h-16" />,
      title: t("welcome_screen.slide_golf_title"),
      description: t("welcome_screen.slide_golf_desc"),
      vehicleId: "golf-cart",
    },
    {
      icon: <RecreationalIcon className="w-16 h-16" />,
      title: t("welcome_screen.slide_rec_title"),
      description: t("welcome_screen.slide_rec_desc"),
      vehicleId: "recreational",
    },
  ];

  const features = [
    { icon: <Star className="w-5 h-5" />, text: t("welcome_screen.feat_five_star") },
    { icon: <Shield className="w-5 h-5" />, text: t("welcome_screen.pill_insured") },
    { icon: <Clock className="w-5 h-5" />, text: t("welcome_screen.feat_on_demand") },
  ];

  const appOptions = [
    { id: "customer", label: t("welcome_screen.portal_customer"), icon: Car, path: "/dashboard", description: t("welcome_screen.portal_customer_desc") },
    { id: "vendor", label: t("welcome_screen.portal_vendor"), icon: Briefcase, path: "/vendor", description: t("welcome_screen.portal_vendor_desc") },
    { id: "admin", label: t("welcome_screen.portal_admin"), icon: LayoutDashboard, path: "/admin", description: t("welcome_screen.portal_admin_desc") },
  ];

  return (
    <div
      data-testid="welcome-screen"
      className="min-h-screen bg-slate-950 flex flex-col"
    >
      {/* Hero Image Section — Rick QA T+19: increased height to fit
          the premium hero copy banner without clipping the logo or
          the vehicle category grid below */}
      <div className="relative h-[60vh] sm:h-[55vh] lg:h-[60vh] min-h-[420px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1663070785797-26d31219249b?w=1200&q=80"
          alt={t("welcome_screen.hero_alt")}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/40 to-slate-950" />
        
        {/* Logo overlay */}
        <div className="absolute top-6 left-6 flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
            <Droplets className="w-5 h-5 text-white" />
          </div>
          <span className="washpros-logo text-2xl" data-testid="welcome-screen-brand-mark">
            <span className="wash">Wash</span>
            <span className="pros">Pros</span>
            <sup className="text-[0.5em] ml-0.5 align-super" data-testid="welcome-screen-brand-tm">™</sup>
          </span>
        </div>

        {/* Theme & Language Selector - Top Right */}
        <div className="absolute top-6 right-6 flex items-center gap-2">
          <ThemeToggle />
          <LanguageSelector />
        </div>

        {/* ─── Premium hero banner overlay (Rick QA T+19, locked variant B) ───
            Locked copy: "A More Luxurious Standard" eyebrow → "Vehicle
            Care, Elevated." headline → premium subheadline → trust
            pills → "Start Your Wash" CTA.
            The hero background is the dark water-drop photo regardless
            of theme, so text inside this overlay uses inline
            color: #ffffff to bypass the global :root.light → dark
            text flip and keep the headline / subheadline bright. */}
        <div
          className="absolute inset-0 flex items-center justify-center px-6 pt-16 pb-6 pointer-events-none"
          data-testid="hero-banner"
        >
          <div className="max-w-2xl w-full text-center pointer-events-auto">
            <p
              className="text-[11px] sm:text-xs uppercase tracking-[0.3em] font-medium mb-2 drop-shadow"
              style={{ color: "hsl(187 94% 70%)" }}
              data-testid="hero-eyebrow"
            >
              {t("welcome_screen.hero_eyebrow")}
            </p>
            <h1
              className="text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight leading-tight drop-shadow-lg"
              style={{ color: "#ffffff" }}
              data-testid="hero-headline"
            >
              {t("welcome_screen.hero_headline_prefix")} <span style={{ color: "hsl(187 94% 70%)" }}>{t("welcome_screen.hero_headline_accent")}</span>
            </h1>
            <p
              className="mt-3 sm:mt-4 text-sm sm:text-base lg:text-lg max-w-xl mx-auto leading-relaxed drop-shadow"
              style={{ color: "rgba(255,255,255,0.92)" }}
              data-testid="hero-subheadline"
            >
              WashPros<sup className="text-[0.6em]">™</sup> {t("welcome_screen.hero_subheadline")}
            </p>

            {/* Trust pills.  Inline colors so the override layer
                doesn't flip them in light mode. */}
            <div
              className="mt-4 sm:mt-5 flex flex-wrap items-center justify-center gap-2"
              data-testid="hero-trust-pills"
            >
              {[
                { icon: <Shield className="w-3.5 h-3.5" />, text: t("welcome_screen.pill_insured") },
                { icon: <Sparkles className="w-3.5 h-3.5" />, text: t("welcome_screen.pill_live") },
                { icon: <Star className="w-3.5 h-3.5" />, text: t("welcome_screen.pill_portals") },
              ].map((pill) => (
                <span
                  key={pill.text}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full backdrop-blur-md text-[11px] sm:text-xs font-medium"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    color: "#ffffff",
                  }}
                >
                  {pill.icon}
                  {pill.text}
                </span>
              ))}
            </div>

            {/* CTA — scrolls to vehicle category grid below */}
            <div className="mt-5 sm:mt-6">
              <Button
                onClick={() => {
                  const target = document.querySelector('[data-testid="welcome-vehicle-grid"]') ||
                                 document.querySelector('[data-testid="vehicle-categories-section"]');
                  if (target) {
                    target.scrollIntoView({ behavior: "smooth", block: "start" });
                  } else {
                    navigate("/dashboard");
                  }
                }}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:shadow-cyan-500/30 hover:shadow-2xl font-semibold px-6 sm:px-8 py-2.5 sm:py-3 rounded-full text-sm sm:text-base transition-all duration-300"
                style={{ color: "#ffffff" }}
                data-testid="hero-start-your-wash-cta"
              >
                {t("welcome_screen.start_your_wash")}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Phase 7g.15.99h (2026-06-11) — Slogans Section was a static
          centered band with `animate-pulse` (fade only, no horizontal
          scroll) — slogans could clip on narrow viewports. Replaced
          inline with the shared `<SloganTicker />` so the welcome
          screen matches the public homepage and the admin/customer/
          vendor portals. The footer slogan at the bottom of this page
          intentionally stays as-is — it's a legal/copyright tagline,
          not a banner. */}
      <SloganTicker speed="normal" />

      {/* Content Section */}
      <div className="flex-1 px-6 py-4 flex flex-col">
        {/* Vehicle Categories - 2x3 Grid */}
        <div className="mb-6">
          <div className="grid grid-cols-3 gap-3 pb-4" data-testid="welcome-vehicle-grid">
            {slides.map((slide, slideIdx) => (
              <button
                key={slide.vehicleId}
                onClick={() => setCurrentSlide(slideIdx)}
                data-testid={`vehicle-${slide.vehicleId}`}
                className={`rounded-xl p-3 flex flex-col items-center justify-center gap-2 transition-all ${
                  currentSlide === slideIdx
                    ? "bg-cyan-500/20 border border-cyan-500/50 text-cyan-400"
                    : "bg-slate-900/50 border border-white/10 text-slate-400 hover:border-white/20"
                }`}
              >
                {React.cloneElement(slide.icon, { className: "w-9 h-9" })}
                <span className="text-xs text-center font-medium leading-tight">{slide.title}</span>
              </button>
            ))}
          </div>
          
          {/* Selected slide description */}
          <div className="text-center mt-3 animate-fade-in">
            <h2 className="text-xl font-bold text-white mb-1">
              {slides[currentSlide].title}
            </h2>
            <p className="text-slate-400 text-sm mb-4">{slides[currentSlide].description}</p>
            <Button
              onClick={() => navigate(`/book?vehicle=${slides[currentSlide].vehicleId}`)}
              data-testid="welcome-book-cta"
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold px-8 py-3 rounded-full hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-300"
            >
              {t("welcome_screen.book_your_wash")}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>

        {/* Features */}
        <div className="flex justify-center gap-6 mb-6">
          {features.map((feature) => (
            <div
              key={feature.text}
              className="flex flex-col items-center gap-2 text-center"
            >
              <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-cyan-400">
                {feature.icon}
              </div>
              <span className="text-xs text-slate-400">{feature.text}</span>
            </div>
          ))}
        </div>

        {/* App Switcher */}
        <div className="mb-6">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 text-center">{t("welcome_screen.choose_portal")}</p>
          <div className="grid grid-cols-3 gap-2">
            {appOptions.map((app) => (
              <button
                key={app.id}
                data-testid={`app-switch-${app.id}`}
                onClick={() => handlePortalClick(app.path)}
                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-900/50 border border-white/10 hover:border-cyan-500/50 hover:bg-cyan-500/10 transition-all"
              >
                <app.icon className="w-6 h-6 text-cyan-400" />
                <span className="text-xs text-white font-medium">{app.label}</span>
                <span className="text-[10px] text-slate-500">{app.description}</span>
              </button>
            ))}
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="mt-auto space-y-3">
          {/* Agreement Checkbox */}
          <div 
            className={`p-4 rounded-xl border transition-all ${
              showAgreementError && !agreedToTerms 
                ? "bg-red-500/10 border-red-500/50" 
                : agreedToTerms 
                  ? "bg-emerald-500/10 border-emerald-500/30" 
                  : "bg-slate-900/50 border-white/10"
            }`}
          >
            <button
              data-testid="agreement-checkbox"
              onClick={() => {
                setAgreedToTerms(!agreedToTerms);
                if (showAgreementError) setShowAgreementError(false);
              }}
              className="flex items-start gap-3 w-full text-left"
            >
              {agreedToTerms ? (
                <CheckSquare className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              ) : (
                <Square className={`w-5 h-5 flex-shrink-0 mt-0.5 ${showAgreementError ? "text-red-400" : "text-slate-500"}`} />
              )}
              <span className="text-sm text-slate-300 leading-relaxed">
                {t("welcome_screen.agree_prefix")}{" "}
                <Link 
                  to="/terms" 
                  onClick={(e) => e.stopPropagation()}
                  className="text-cyan-400 hover:text-cyan-300 underline inline-flex items-center gap-1"
                  data-testid="terms-link"
                >
                  {t("welcome_screen.terms_link")}
                  <ExternalLink className="w-3 h-3" />
                </Link>
                ,{" "}
                <Link 
                  to="/privacy" 
                  onClick={(e) => e.stopPropagation()}
                  className="text-cyan-400 hover:text-cyan-300 underline inline-flex items-center gap-1"
                  data-testid="privacy-link"
                >
                  {t("welcome_screen.privacy_link")}
                  <ExternalLink className="w-3 h-3" />
                </Link>
                ,{" "}
                <Link 
                  to="/copyright-policy" 
                  onClick={(e) => e.stopPropagation()}
                  className="text-cyan-400 hover:text-cyan-300 underline inline-flex items-center gap-1"
                  data-testid="copyright-policy-link"
                >
                  {t("welcome_screen.copyright_link")}
                  <ExternalLink className="w-3 h-3" />
                </Link>
                , {t("welcome_screen.and_word")}{" "}
                <span className="text-amber-400">{t("welcome_screen.billing_terms")}</span>
              </span>
            </button>
            {showAgreementError && !agreedToTerms && (
              <p className="text-red-400 text-xs mt-2 ml-8" data-testid="agreement-error">
                {t("welcome_screen.accept_terms_error")}
              </p>
            )}
          </div>

          <Button
            data-testid="get-started-btn"
            onClick={handleGetStarted}
            className="w-full btn-primary h-14 text-lg"
          >
            {t("auth.continue_with_google")}
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
          
          {/* Email Login Option */}
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-slate-500 text-xs">{t("common.or")}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <Link
              to="/auth/login"
              data-testid="email-login-btn"
              className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-white/20 text-white hover:bg-white/5 transition-colors"
            >
              <Mail className="w-4 h-4 text-cyan-400" />
              <span className="text-sm">{t("welcome_screen.email_btn")}</span>
            </Link>
            
            <Link
              to="/auth/login?mode=phone"
              data-testid="phone-login-btn"
              className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-white/20 text-white hover:bg-white/5 transition-colors"
            >
              <Phone className="w-4 h-4 text-emerald-400" />
              <span className="text-sm">{t("welcome_screen.phone_btn")}</span>
            </Link>
          </div>
          
          <p className="text-center text-slate-500 text-xs">
            {t("welcome_screen.agreement_recorded")}
          </p>
          
          <div className="text-center pt-2">
            <SocialMediaLinks className="justify-center mb-3" showLabels={true} />
            <p className="text-cyan-400 text-sm font-medium">washpros.io</p>
            <p className="text-slate-500 text-xs mt-1">{SLOGANS.primary}</p>
            <p className="text-slate-600 text-[10px] mt-2">WashPros<sup className="text-[0.5em]">™</sup> | {t("welcome_screen.subsidiary_note")} <span className="text-slate-500">WashPros Global Services LLC</span></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
